import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { TiArrowLeftThick } from 'react-icons/ti'

export const Route = createFileRoute('/swgen')({
  component: SwgenPage,
})

// Constants
const POINT_RADIUS = 6
const MIN_RADIUS_COUNT = 5
const MAX_RADIUS_COUNT = 10
const MIN_SPIRAL_WRAPS = 4
const MAX_SPIRAL_WRAPS = 25
const MIN_SPIRAL_DROOP = 1
const MAX_SPIRAL_DROOP = 80

// Validation constants
const RADII_OK = 0
const RADII_NUMBER_ERROR = 1
const RADII_SHAPE_ERROR = 2
const RADII_CONTAINER_ERROR = 3

// Chirality constants
const CLOCKWISE = 0
const COUNTER_CLOCKWISE = 1

interface Point {
  x: number
  y: number
}

function SwgenPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [step, setStep] = useState(1)
  const [hubCoordinates, setHubCoordinates] = useState<Point[]>([])
  const [radiiEnds, setRadiiEnds] = useState<Point[]>([])
  const [chirality, setChirality] = useState(CLOCKWISE)
  const [spiralWraps, setSpiralWraps] = useState(Math.round((MIN_SPIRAL_WRAPS + MAX_SPIRAL_WRAPS) / 2))
  const [spiralDroop, setSpiralDroop] = useState(Math.round((MIN_SPIRAL_DROOP + MAX_SPIRAL_DROOP) / 2))
  const [modalType, setModalType] = useState<string | null>(null)
  const [deleteMode, setDeleteMode] = useState(false)
  const [dragging, setDragging] = useState<{ list: 'hub' | 'radii', index: number, original: Point } | null>(null)

  // Point-in-polygon algorithm from https://github.com/substack/point-in-polygon
  const pointInPolygon = (point: number[], vs: number[][]): boolean => {
    const x = point[0], y = point[1]
    let inside = false
    const len = vs.length
    for (let i = 0, j = len - 1; i < len; j = i++) {
      const xi = vs[i][0], yi = vs[i][1]
      const xj = vs[j][0], yj = vs[j][1]
      const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)
      if (intersect) inside = !inside
    }
    return inside
  }

  const getCanvasCoords = (clickPx: Point, canvas: HTMLCanvasElement): Point => {
    const rect = canvas.getBoundingClientRect()
    return {
      x: clickPx.x / rect.width,
      y: clickPx.y / rect.height
    }
  }

  const canvasCoordsToPx = (coords: Point, canvas: HTMLCanvasElement): Point => {
    const rect = canvas.getBoundingClientRect()
    return {
      x: coords.x * rect.width,
      y: coords.y * rect.height
    }
  }

  const quadraticDistance = (a: Point, b: Point): number => {
    return Math.sqrt(Math.pow(b.x - a.x, 2) + Math.pow(b.y - a.y, 2))
  }

  const findPointClicked = (list: Point[], clickPx: Point, canvas: HTMLCanvasElement): { point: Point | null, index: number } => {
    for (let i = 0; i < list.length; i++) {
      const ptPx = canvasCoordsToPx(list[i], canvas)
      const dist = quadraticDistance(clickPx, ptPx)
      if (dist <= POINT_RADIUS) {
        return { point: list[i], index: i }
      }
    }
    return { point: null, index: -1 }
  }

  const getHubRadiiRing = (): Point[] => {
    if (hubCoordinates.length === 0) return []

    const hub = hubCoordinates[0]
    let pointsRelativeToHub = radiiEnds.map(r => ({
      x: r.x - hub.x,
      y: r.y - hub.y
    }))

    let anglesFromHub = pointsRelativeToHub.map(p => Math.atan2(p.y, p.x))
    anglesFromHub = anglesFromHub.map(r => r < 0 ? r + Math.PI * 2 : r)

    const pointsWithAngles = pointsRelativeToHub.map((p, i) => ({
      ...p,
      radians: anglesFromHub[i]
    }))

    pointsWithAngles.sort((a, b) => a.radians - b.radians)

    return pointsWithAngles.map(p => ({ x: p.x, y: p.y }))
  }

  const validateRadii = (): number => {
    if (radiiEnds.length < MIN_RADIUS_COUNT || radiiEnds.length > MAX_RADIUS_COUNT) {
      return RADII_NUMBER_ERROR
    }

    const hubRadiiRing = getHubRadiiRing()
    const zCrossProducts: number[] = []

    for (let i = 0; i < hubRadiiRing.length; i++) {
      const a = hubRadiiRing[i]
      const b = hubRadiiRing[(i + 1) % hubRadiiRing.length]
      const c = hubRadiiRing[(i + 2) % hubRadiiRing.length]

      const d1 = { x: b.x - a.x, y: b.y - a.y }
      const d2 = { x: c.x - b.x, y: c.y - b.y }

      zCrossProducts[i] = (d1.x * d2.y) - (d1.y * d2.x)
    }

    for (let i = 0; i < zCrossProducts.length - 1; i++) {
      if (zCrossProducts[i] * zCrossProducts[i + 1] < 0) {
        return RADII_SHAPE_ERROR
      }
    }

    const hub = hubCoordinates[0]
    const polygonPoints = hubRadiiRing.map(r => [r.x + hub.x, r.y + hub.y])

    if (!pointInPolygon([hub.x, hub.y], polygonPoints)) {
      return RADII_CONTAINER_ERROR
    }

    return RADII_OK
  }

  const refitCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    const width = canvas.clientWidth
    const height = canvas.clientHeight
    canvas.width = width
    canvas.height = height
  }

  const redraw = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    refitCanvas()
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    if (step === 1 || step === 2) {
      drawHub(ctx, canvas)
    }

    if (step === 2) {
      drawRadiiEnds(ctx, canvas)
    }

    if (step === 3) {
      drawRadiiLines(ctx, canvas)
      drawSpiral(ctx, canvas)
    }
  }

  const drawHub = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => {
    if (hubCoordinates.length === 0) return

    const hubPx = canvasCoordsToPx(hubCoordinates[0], canvas)
    ctx.fillStyle = '#ccddff'
    ctx.beginPath()
    ctx.ellipse(hubPx.x, hubPx.y, POINT_RADIUS, POINT_RADIUS, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.strokeStyle = 'black'
    ctx.stroke()
  }

  const drawRadiiEnds = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => {
    radiiEnds.forEach(r => {
      const rPx = canvasCoordsToPx(r, canvas)
      ctx.fillStyle = '#aaffaa'
      ctx.beginPath()
      ctx.ellipse(rPx.x, rPx.y, POINT_RADIUS, POINT_RADIUS, 0, 0, Math.PI * 2)
      ctx.fill()
      ctx.strokeStyle = 'black'
      ctx.stroke()
    })
  }

  const drawRadiiLines = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => {
    const hubPx = canvasCoordsToPx(hubCoordinates[0], canvas)
    radiiEnds.forEach(r => {
      const rPx = canvasCoordsToPx(r, canvas)
      ctx.beginPath()
      ctx.moveTo(hubPx.x, hubPx.y)
      ctx.lineTo(rPx.x, rPx.y)
      ctx.lineWidth = 2
      ctx.strokeStyle = 'black'
      ctx.stroke()
    })
  }

  const drawSpiral = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => {
    const hubPx = canvasCoordsToPx(hubCoordinates[0], canvas)
    let ringRadiiPx = getHubRadiiRing().map(r => canvasCoordsToPx(r, canvas))

    if (chirality === COUNTER_CLOCKWISE) {
      ringRadiiPx = ringRadiiPx.reverse()
    }

    ctx.beginPath()
    ctx.moveTo(hubPx.x, hubPx.y)
    let prevPx = hubPx

    for (let i = 0; i < spiralWraps; i++) {
      ringRadiiPx.forEach((r, j) => {
        const scaleFactor = ((i + ((j + 1) / ringRadiiPx.length)) / spiralWraps)
        const thisPx = {
          x: hubPx.x + (scaleFactor * r.x),
          y: hubPx.y + (scaleFactor * r.y)
        }

        const midPtPx = {
          x: (thisPx.x + prevPx.x) / 2,
          y: (thisPx.y + prevPx.y) / 2
        }

        const vectorToHubPx = {
          x: hubPx.x - midPtPx.x,
          y: hubPx.y - midPtPx.y
        }

        const ctrlPx = {
          x: midPtPx.x + vectorToHubPx.x * (spiralDroop / 100),
          y: midPtPx.y + vectorToHubPx.y * (spiralDroop / 100)
        }

        ctx.quadraticCurveTo(ctrlPx.x, ctrlPx.y, thisPx.x, thisPx.y)
        prevPx = thisPx
      })
    }

    ctx.lineWidth = 1
    ctx.strokeStyle = 'black'
    ctx.stroke()
  }

  const handleCanvasClick = (evt: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const clickPx = 'touches' in evt
      ? {
        x: evt.touches[0].clientX - rect.left,
        y: evt.touches[0].clientY - rect.top
      }
      : {
        x: evt.clientX - rect.left,
        y: evt.clientY - rect.top
      }

    if (deleteMode) {
      const { index } = findPointClicked(radiiEnds, clickPx, canvas)
      if (index >= 0) {
        setRadiiEnds(prev => prev.filter((_, i) => i !== index))
      }
      setDeleteMode(false)
      return
    }

    if (step === 1 || step === 2) {
      if (hubCoordinates.length === 0) {
        const { point } = findPointClicked(hubCoordinates, clickPx, canvas)
        if (!point) {
          setHubCoordinates([getCanvasCoords(clickPx, canvas)])
        }
      }
    }

    if (step === 2) {
      const { point } = findPointClicked(radiiEnds, clickPx, canvas)
      if (!point && radiiEnds.length < MAX_RADIUS_COUNT) {
        const newCoord = getCanvasCoords(clickPx, canvas)
        const tooClose = [...radiiEnds, ...hubCoordinates].some(p => {
          const pPx = canvasCoordsToPx(p, canvas)
          return quadraticDistance(clickPx, pPx) <= (2 * POINT_RADIUS)
        })
        if (!tooClose) {
          setRadiiEnds(prev => [...prev, newCoord])
        }
      }
    }
  }

  const handleCanvasMouseDown = (evt: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return

    evt.preventDefault()

    const rect = canvas.getBoundingClientRect()
    const clickPx = 'touches' in evt
      ? {
        x: evt.touches[0].clientX - rect.left,
        y: evt.touches[0].clientY - rect.top
      }
      : {
        x: evt.clientX - rect.left,
        y: evt.clientY - rect.top
      }

    if (deleteMode) return

    if (step === 1 || step === 2 || step === 3) {
      const { point, index } = findPointClicked(hubCoordinates, clickPx, canvas)
      if (point) {
        setDragging({ list: 'hub', index, original: { ...point } })
      }
    }

    if (step === 2) {
      const { point, index } = findPointClicked(radiiEnds, clickPx, canvas)
      if (point) {
        setDragging({ list: 'radii', index, original: { ...point } })
      }
    }
  }

  const handleCanvasMouseMove = (evt: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!dragging) return
    const canvas = canvasRef.current
    if (!canvas) return

    evt.preventDefault()

    const rect = canvas.getBoundingClientRect()
    const movePx = 'touches' in evt
      ? {
        x: evt.touches[0].clientX - rect.left,
        y: evt.touches[0].clientY - rect.top
      }
      : {
        x: evt.clientX - rect.left,
        y: evt.clientY - rect.top
      }

    const newCoords = getCanvasCoords(movePx, canvas)

    if (dragging.list === 'hub') {
      setHubCoordinates(prev => prev.map((p, i) => i === dragging.index ? newCoords : p))
    } else {
      setRadiiEnds(prev => prev.map((p, i) => i === dragging.index ? newCoords : p))
    }
  }

  const handleCanvasMouseUp = (evt: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!dragging) return
    const canvas = canvasRef.current
    if (!canvas) return

    evt.preventDefault()

    const rect = canvas.getBoundingClientRect()
    const upPx = 'changedTouches' in evt
      ? {
        x: evt.changedTouches[0].clientX - rect.left,
        y: evt.changedTouches[0].clientY - rect.top
      }
      : {
        x: evt.clientX - rect.left,
        y: evt.clientY - rect.top
      }

    // Check if too close to other points
    const considerations = dragging.list === 'hub' ? radiiEnds : [...hubCoordinates, ...radiiEnds.filter((_, i) => i !== dragging.index)]
    const tooClose = considerations.some(p => {
      const pPx = canvasCoordsToPx(p, canvas)
      return quadraticDistance(upPx, pPx) <= (5 * POINT_RADIUS)
    })

    if (tooClose) {
      // Reset to original position
      if (dragging.list === 'hub') {
        setHubCoordinates(prev => prev.map((p, i) => i === dragging.index ? dragging.original : p))
      } else {
        setRadiiEnds(prev => prev.map((p, i) => i === dragging.index ? dragging.original : p))
      }
    }

    setDragging(null)
  }

  const saveImage = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    const link = document.createElement('a')
    const now = new Date()
    link.download = `spiderweb-${now.getMonth() + 1}-${now.getDate()}-${now.getFullYear()}-${now.getHours()}-${now.getMinutes()}-${now.getSeconds()}.png`
    link.href = canvas.toDataURL()
    link.click()
  }

  useEffect(() => {
    redraw()
  }, [step, hubCoordinates, radiiEnds, chirality, spiralWraps, spiralDroop])

  useEffect(() => {
    const handleResize = () => redraw()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [step, hubCoordinates, radiiEnds, chirality, spiralWraps, spiralDroop])

  return (
    <div className="min-h-screen md:min-h-0">
      <div className="md:max-w-7xl md:mx-auto md:px-4 md:sm:px-6 md:lg:px-8 md:py-8">
        <div className="bg-white md:rounded-lg md:shadow-md overflow-hidden md:min-h-[700px] flex flex-col min-h-screen md:min-h-0 md:h-auto overflow-y-auto md:overflow-visible">
          <div className="bg-gray-100 border-b border-gray-200 px-6 py-4 flex-shrink-0">
            <div className="flex items-center gap-4 mb-2">
              <img src="spiderweb.png" width="40" alt="Spiderweb" />
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">SWGen—John Chilton's Spiderweb Generator</h2>
            </div>
            <a href="/" className="text-md text-blue-600 hover:text-blue-800 flex items-center gap-2"><TiArrowLeftThick/> Return to homepage</a>
          </div>

          <div className="flex flex-col md:flex-row md:items-start p-4 md:p-6 gap-4 md:gap-6 flex-1 md:flex-initial min-h-0 md:min-h-fit pb-safe md:pb-6">
            {/* Configuration Panel - appears first on mobile, right on desktop */}
            <div className="w-full md:w-96 md:flex-shrink-0 order-1 md:order-2 flex-shrink-0">
              <div className="bg-gray-600 text-white px-4 py-2 font-semibold rounded-t">Instructions</div>
              <div className="bg-gray-100 border border-gray-300 rounded-b p-4">
                {step === 1 && (
                  <div className="p-4 bg-white rounded shadow-sm">
                    <div className="text-lg font-semibold mb-3 text-gray-900">Step 1: Place the hub</div>
                    <div className="mb-3 text-gray-700">Click the canvas to place the hub of the web. It can be dragged once placed. The hub is the center of your spiderweb.</div>
                    <div className="mb-4 flex items-center gap-2 text-gray-700">
                      <input
                        type="checkbox"
                        checked={hubCoordinates.length > 0}
                        readOnly
                        className="w-4 h-4"
                      />
                      <label>Hub placed</label>
                    </div>
                    <button
                      onClick={() => setStep(2)}
                      disabled={hubCoordinates.length === 0}
                      className={`px-4 py-2 rounded font-medium ${hubCoordinates.length > 0
                        ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                      Next
                    </button>
                  </div>
                )}

                {step === 2 && (
                  <div>
                    <button onClick={() => setStep(1)} className="mb-3 px-4 py-2 flex items-center gap-2 align-middle cursor-pointer bg-white border border-gray-300 rounded hover:bg-gray-50 text-gray-700"><TiArrowLeftThick/> Back</button>
                    <div className="p-4 bg-white rounded shadow-sm">
                      <div className="text-lg font-semibold mb-3 text-gray-900">Step 2: Place radii ends</div>
                      <div className="mb-3 text-gray-700">
                        Click the canvas to place radii ends. Click and drag to move ones already placed.
                        The radii must all form a convex polygon with the hub inside. There must be at least {MIN_RADIUS_COUNT}, but no more than {MAX_RADIUS_COUNT}.
                      </div>
                      <button onClick={() => setModalType('place-radii-help')} className="mb-4 px-4 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-gray-700 cursor-pointer">More information</button><br />
                      <button
                        onClick={() => {
                          if (radiiEnds.length === 0) {
                            setModalType('no-points-error')
                          } else {
                            setDeleteMode(true)
                          }
                        }}
                        className="mb-2 mr-2 px-4 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-gray-700 cursor-pointer"
                      >
                        Delete one
                      </button>
                      <button onClick={() => setRadiiEnds([])} className="mb-2 px-4 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-gray-700 cursor-pointer">Reset</button><br />
                      <button
                        onClick={() => {
                          const result = validateRadii()
                          if (result === RADII_OK) {
                            setStep(3)
                          } else if (result === RADII_NUMBER_ERROR) {
                            setModalType('radii-number-error')
                          } else if (result === RADII_SHAPE_ERROR) {
                            setModalType('radii-shape-error')
                          } else if (result === RADII_CONTAINER_ERROR) {
                            setModalType('radii-container-error')
                          }
                        }}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium cursor-pointer"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div>
                    <button onClick={() => setStep(2)} className="mb-3 flex items-center gap-2 align-middle cursor-pointer px-4 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-gray-700"><TiArrowLeftThick/> Back</button>
                    <div className="p-4 bg-white rounded shadow-sm">
                      <div className="text-lg font-semibold mb-3 text-gray-900">Step 3: Finishing touches</div>
                      <div className="mb-4 text-gray-700">Here are some final options to adjust the look of the spiderweb.</div>

                      <div className="text-base font-semibold mb-2 text-gray-900">Adjust chirality (handedness)</div>
                      <div className="mb-4 text-gray-700">
                        <div>
                          <input
                            type="radio"
                            id="clockwise"
                            checked={chirality === CLOCKWISE}
                            onChange={() => setChirality(CLOCKWISE)}
                            className="mr-2 cursor-pointer"
                          />
                          <label htmlFor="clockwise" className="cursor-pointer">Clockwise</label>
                        </div>
                        <div>
                          <input
                            type="radio"
                            id="counter-clockwise"
                            checked={chirality === COUNTER_CLOCKWISE}
                            onChange={() => setChirality(COUNTER_CLOCKWISE)}
                            className="mr-2 cursor-pointer"
                          />
                          <label htmlFor="counter-clockwise" className="cursor-pointer">Counter-clockwise</label>
                        </div>
                      </div>

                      <div className="text-base font-semibold mb-2 text-gray-900">Adjust spiral winding</div>
                      <div className="mb-4 flex items-center gap-2">
                        <label className="text-sm text-gray-600">Loose</label>
                        <input
                          type="range"
                          min={MIN_SPIRAL_WRAPS}
                          max={MAX_SPIRAL_WRAPS}
                          value={spiralWraps}
                          onChange={(e) => setSpiralWraps(Number(e.target.value))}
                          className="flex-1 cursor-pointer"
                        />
                        <label className="text-sm text-gray-600">Tight</label>
                      </div>

                      <div className="text-base font-semibold mb-2 text-gray-900">Adjust spiral droop</div>
                      <div className="mb-4 flex items-center gap-2">
                        <label className="text-sm text-gray-600">Loose</label>
                        <input
                          type="range"
                          min={MIN_SPIRAL_DROOP}
                          max={MAX_SPIRAL_DROOP}
                          value={MAX_SPIRAL_DROOP + MIN_SPIRAL_DROOP - spiralDroop}
                          onChange={(e) => setSpiralDroop(MAX_SPIRAL_DROOP + MIN_SPIRAL_DROOP - Number(e.target.value))}
                          className="flex-1 cursor-pointer"
                        />
                        <label className="text-sm text-gray-600">Tight</label>
                      </div>

                      <button onClick={saveImage} className="cursor-pointer mt-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium">Save Image</button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Canvas - appears second on mobile, left on desktop */}
            <canvas
              ref={canvasRef}
              onClick={handleCanvasClick}
              onMouseDown={handleCanvasMouseDown}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onTouchStart={handleCanvasMouseDown as any}
              onTouchMove={handleCanvasMouseMove as any}
              onTouchEnd={handleCanvasMouseUp as any}
              className="w-full md:min-w-[500px] aspect-square md:aspect-auto md:flex-1 md:min-h-[400px] border-2 border-gray-300 bg-white cursor-pointer rounded order-2 md:order-1 touch-none"
              style={deleteMode ? { cursor: `url("delete.png"), auto` } : {}}
            />
          </div>
        </div>

        {modalType && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-xl">
              <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-xl font-semibold text-gray-900">
                  {modalType === 'incomplete-hub-error' && 'Incomplete'}
                  {modalType === 'no-points-error' && 'No Points'}
                  {modalType === 'place-radii-help' && 'About Placing Radii Ends'}
                  {modalType === 'radii-number-error' && 'Radius Number Error'}
                  {modalType === 'radii-shape-error' && 'Radius Placement Error'}
                  {modalType === 'radii-container-error' && 'Radius Placement Error'}
                </h3>
                <button onClick={() => setModalType(null)} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none">&times;</button>
              </div>
              <div className="p-6 text-gray-700">
                {modalType === 'incomplete-hub-error' && <p>This step is incomplete. You have to click on the canvas to add a web hub.</p>}
                {modalType === 'no-points-error' && <p>You can't remove points— there aren't any placed yet.</p>}
                {modalType === 'radii-number-error' && <p>There must be at least {MIN_RADIUS_COUNT} but no more than {MAX_RADIUS_COUNT} radius ends.</p>}
                {modalType === 'radii-shape-error' && (
                  <p>The radius endpoints are not properly formed. They must form a convex polygon. For an explanation of what that is, please see <a href="https://mathworld.wolfram.com/ConvexPolygon.html" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Wolfram's MathWorld encyclopedia article on convex polygons</a>.</p>
                )}
                {modalType === 'radii-container-error' && <p>The radius endpoints form a convex polygon but they do not contain the web hub. Move them around so that they form a ring around the hub.</p>}
                {modalType === 'place-radii-help' && (
                  <div className="space-y-3">
                    <p>For information on what "radii ends" are, visit <a href="http://www.brisbaneinsects.com/brisbane_weavers/SpiderWeb.htm" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">this spiderweb education</a> page.</p>
                    <p>These points cannot be placed too close to each other or to the hub. They can be dragged after being placed.</p>
                    <p>To delete one, click the "Delete One" button; the cursor will become a "delete" icon until pressing the Escape or Backspace key or clicking. It will revert when you click anywhere on the page, but if you click on a radius endpoint, that point will be deleted.</p>
                    <p>To delete all of them and start this step over, click the "Reset" button.</p>
                    <p>The radius points must form a <i>convex polygon</i> with the hub inside (that the hub is inside is tested using <a href="https://github.com/substack/point-in-polygon" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">substack's point-in-polygon algorithm</a>, licensed under the <a href="https://github.com/substack/point-in-polygon/blob/master/LICENSE" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">MIT License</a>).</p>
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <button onClick={() => setModalType(null)} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium">Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
